//
import type { AuthData, CreatePasswordData, ForgotPasswordData, TokenData } from '@/types/Models/Account'
import type { AuthStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { catchErrors, none, resolveHomePage } from '@/services/Helpers'
import { useAccountStore } from '@/store/account'
import { useCustomersStore } from '@/store/customers'
import { LOGIN_PAGE } from '@/types/router'
import Axios from '@/services/Axios'
import router from '@/router'
import AuthProxy from '@/proxies/AuthProxy'
import CompanyProxy from '@/proxies/CompanyProxy'

const setAuthorizationHeader = (token: string | null) => {
  if (token) {
    Axios.defaults.headers.common.Authorization = `Bearer ${token}`
  } else {
    delete Axios.defaults.headers.common.Authorization
  }

  const echoAuthHeaders = (window as any)?.Echo?.connector?.options?.auth?.headers
  if (echoAuthHeaders) {
    echoAuthHeaders.Authorization = token ? `Bearer ${token}` : ''
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreState => ({
    authenticated: false,
  }),
  actions: {
    applyToken(token: string) {
      localStorage.setItem('id_token', token)
      setAuthorizationHeader(token)
      this.authenticated = true
    },
    clearAuth() {
      this.authenticated = false
      localStorage.removeItem('id_token')
      setAuthorizationHeader(null)
    },
    async check() {
      const token = localStorage.getItem('id_token') || ''

      if (!token) {
        this.authenticated = false
        return
      }

      setAuthorizationHeader(token)

      try {
        const accountStore = useAccountStore()
        await accountStore.me()
        if (accountStore.isCustomer) {
          await useCustomersStore().me()
        }
        this.authenticated = true
      } catch {
        localStorage.removeItem('id_token')
        setAuthorizationHeader(null)
        this.authenticated = false
      }
    },
    handleLoginSuccess(token: string) {
      this.applyToken(token)
      const accountStore = useAccountStore()
      return accountStore.me().then(async () => {
        if (accountStore.isCustomer) {
          await useCustomersStore().me()
        }
        this.authenticated = true
      })
    },
    loginByToken(token: string) {
      return this.handleLoginSuccess(token).then(() => {
        const accountStore = useAccountStore()
        const redirectQuery = router.currentRoute.value.query.redirect
        const redirectPath = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery
        const account = accountStore.account ?? null
        const homePage = resolveHomePage(account)

        if (redirectPath && redirectPath !== '/') {
          router.push(redirectPath).catch(none)
          return
        }

        router.push({ name: homePage }).catch(none)
      })
    },
    login(payload: AuthData) {
      return new AuthProxy().login(payload).then((response: TokenData) => {
        if (response.access_token) {
          return this.handleLoginSuccess(response.access_token).then(() => response)
        }

        return response
      })
    },
    logout() {
      return new AuthProxy()
        .logout()
        .catch((error) => {
          if (import.meta.env.DEV) {
            console.error(error)
          }
        })
        .finally(() => {
          this.clearAuth()
          const accountStore = useAccountStore()
          accountStore.reset()
          useCustomersStore().customer = null
          router.push({ name: LOGIN_PAGE }).catch((error) => {
            if (error.name !== 'NavigationDuplicated' && !error.message.includes('Navigation cancelled')) {
              console.error(error)
            }
          })
        })
    },
    forgotPassword(payload: ForgotPasswordData) {
      return new AuthProxy().forgotPassword(payload)
    },
    createPassword(payload: CreatePasswordData) {
      return new Promise((resolve, reject) => {
        new AuthProxy()
          .createPassword(payload)
          .then((response) => resolve(response))
          .catch((error) => reject(error.errors))
      })
    },
    authAsCompany(id: string, userId?: string | null) {
      return new CompanyProxy()
        .authAsCompany(id, userId)
        .then((response) => {
          if (response.access_token) {
            return this.handleLoginSuccess(response.access_token).then(() => {
              const accountStore = useAccountStore()
              const redirectQuery = router.currentRoute.value.query.redirect
              const redirectPath = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery
              const account = accountStore.account ?? null
              const homePage = resolveHomePage(account)

              if (redirectPath && redirectPath !== '/') {
                router.push(redirectPath).catch(none)
                return response
              }

              router.push({ name: homePage }).catch(none)

              return response
            })
          }

          return response
        })
        .catch((error: any) => {
          catchErrors(error)
          throw error
        })
    },
  },
})

export type AuthStore = ReturnType<typeof useAuthStore>
